// StepRow.tsx — a compact lesson row (used in Journey + Module detail).
// The leading disc encodes state: completed (check), a video (play), or a
// reading/reflection glyph. Amber marks the current step.
import { Display, Meta, Pressable, GlyphWell, PlayDisc } from './ui';
import { Icon, type IconName } from './icons';
import type { Step, StepStatus } from '@/types';

const KIND: Record<string, { icon: IconName; label: string }> = {
  video: { icon: 'film', label: 'Video' },
  article: { icon: 'book', label: 'Reading' },
  reflection: { icon: 'reflections', label: 'Reflection' },
  exercise: { icon: 'leaf', label: 'Exercise' },
  audio: { icon: 'audio', label: 'Audio' },
  mixed: { icon: 'sparkle', label: 'Lesson' },
};

function metaLabel(step: Step): string {
  if (step.type === 'video' && step.duration) return `${Math.round(step.duration / 60)} min`;
  if (step.type === 'article' && step.read_minutes) return `${step.read_minutes} min read`;
  if (step.type === 'reflection') return '2 min';
  if (step.type === 'audio' && step.duration) return `${Math.round(step.duration / 60)} min`;
  return '';
}

function StepDisc({ step, status, onOpen }: { step: Step; status: StepStatus; onOpen: () => void }) {
  if (status === 'done') {
    return (
      <div
        style={{
          width: 42,
          height: 42,
          borderRadius: 999,
          background: 'rgba(29,158,117,0.12)',
          color: 'var(--green-deep)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: 'inset 0 0 0 1px rgba(29,158,117,0.2)',
        }}
      >
        <Icon name="check" size={17} sw={2.2} />
      </div>
    );
  }
  if (step.type === 'video') {
    return <PlayDisc size={42} variant={status === 'current' ? 'amber' : 'green'} onClick={onOpen} label="Play lesson" />;
  }
  // The current step always reads amber (active), even for non-video kinds.
  const amber = status === 'current' || step.type === 'article';
  const tone = amber ? 'amber' : 'green';
  const color = amber ? 'var(--amber-deep)' : 'var(--green-deep)';
  return (
    <GlyphWell size={42} radius={13} tone={tone} color={color}>
      <Icon name={KIND[step.type]?.icon ?? 'sparkle'} size={19} />
    </GlyphWell>
  );
}

function stepTitleText(step: Step): string {
  const t = step.title as { pre?: string; em?: string; post?: string };
  return [t?.pre, t?.em, t?.post].filter(Boolean).join('').trim();
}

export function StepRow({
  step,
  status,
  onOpen,
  last,
  disabled,
  lockedUntilTomorrow,
}: {
  step: Step;
  status: StepStatus;
  onOpen: () => void;
  last?: boolean;
  disabled?: boolean;
  lockedUntilTomorrow?: boolean;
}) {
  const kind = KIND[step.type] ?? KIND.mixed;
  const meta = metaLabel(step);
  const current = status === 'current' && !lockedUntilTomorrow;
  return (
    <Pressable
      onClick={disabled ? undefined : onOpen}
      ariaLabel={lockedUntilTomorrow ? `${stepTitleText(step)}, opens tomorrow` : current ? `Continue: ${kind.label}` : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 13,
        // The current step is lifted into a clearly highlighted block (amber wash +
        // ring) so "where I am / continue here" reads at a glance on the vertical list.
        padding: current ? '12px 12px' : '11px 4px',
        margin: current ? '2px 0' : 0,
        borderRadius: current ? 14 : 0,
        background: current ? 'rgba(200,134,26,0.10)' : 'none',
        boxShadow: current ? 'inset 0 0 0 1.5px rgba(200,134,26,0.34)' : 'none',
        borderBottom: current ? 'none' : last ? 'none' : '1px solid var(--hair)',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <StepDisc
        step={step}
        status={status}
        onOpen={() => {
          if (!disabled) onOpen();
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', marginBottom: 2, whiteSpace: 'nowrap' }}>
          <span
            style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: status === 'current' ? 'var(--amber-deep)' : 'var(--ink-soft)',
            }}
          >
            {kind.label}
          </span>
          {meta && <Meta style={{ opacity: 0.75 }}>· {meta}</Meta>}
        </div>
        <Display parts={step.title} size={15} lh={1.2} />
      </div>
      {lockedUntilTomorrow ? (
        <span
          style={{
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--ink-soft)',
          }}
        >
          <Icon name="lock" size={13} />
          Tomorrow
        </span>
      ) : current ? (
        <span
          style={{
            flexShrink: 0,
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: 11,
            fontWeight: 700,
            color: '#fff',
            background: 'var(--amber-deep)',
            padding: '5px 11px',
            borderRadius: 999,
          }}
        >
          Continue
        </span>
      ) : (
        <span style={{ color: 'var(--ink-soft)', opacity: 0.4, flexShrink: 0 }}>
          <Icon name="chevRight" size={16} />
        </span>
      )}
    </Pressable>
  );
}
